import { useCallback, useEffect, useState, type CSSProperties } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { pingCrm, supabaseHost, type CrmPingResult } from '../../lib/supabase';

// Sticky warning shown on every page when the CRM backend can't be reached or
// rejects the key. Replaces the old failure mode where a misconfigured deploy
// just left lists spinning on "Lade…" with no explanation.
export function ConnectionBanner() {
  const [result, setResult] = useState<CrmPingResult | null>(null);
  const [checking, setChecking] = useState(false);

  const check = useCallback(async () => {
    setChecking(true);
    const r = await pingCrm();
    setResult(r);
    setChecking(false);
  }, []);

  useEffect(() => {
    void check();
  }, [check]);

  if (!result || result.ok) return null;

  const message =
    result.status === 401 || result.status === 403
      ? `Das CRM-Backend (${supabaseHost}) lehnt den Zugriff ab — der hinterlegte Schlüssel passt nicht zum Projekt.`
      : `Keine Verbindung zum CRM-Backend (${supabaseHost}). Die App zeigt nur zwischengespeicherte Daten. Bitte die Deploy-Konfiguration prüfen und neu ausrollen.`;

  return (
    <div role="alert" style={bannerStyle}>
      <AlertTriangle size={18} style={{ flexShrink: 0 }} />
      <span style={{ flex: 1 }}>{message}</span>
      <button onClick={check} disabled={checking} style={btnStyle}>
        <RefreshCw size={14} />
        {checking ? 'Prüfe…' : 'Erneut prüfen'}
      </button>
    </div>
  );
}

// Full-screen notice when the connection isn't configured at all (missing or
// placeholder build variables) — instead of an empty page or a login loop.
export function SupabaseConfigError({ reason }: { reason: string }) {
  return (
    <div style={screenStyle}>
      <div style={cardStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
          <AlertTriangle size={24} color="#aa1a1d" />
          <h1 style={{ margin: 0, fontSize: 20 }}>CRM-Verbindung nicht konfiguriert</h1>
        </div>
        <p style={{ margin: '0 0 12px', lineHeight: 1.5 }}>{reason}</p>
        <p style={{ margin: '0 0 8px', lineHeight: 1.5 }}>
          Das Callcenter braucht die Verbindung zum aktuellen CRM-Projekt
          („Randori-Pro Data"). Diese Werte werden beim Build gesetzt:
        </p>
        <pre style={preStyle}>
{`VITE_SUPABASE_URL=https://cofhixwcaiulxvohccob.supabase.co
VITE_SUPABASE_PUBLISHABLE_KEY=<Publishable-Key des Projekts>`}
        </pre>
        <p style={{ margin: '12px 0 0', fontSize: 13, color: '#666', lineHeight: 1.5 }}>
          In Coolify als <strong>Build Variables</strong> setzen und mit
          „Force Rebuild" neu ausrollen.
        </p>
      </div>
    </div>
  );
}

const bannerStyle: CSSProperties = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  zIndex: 9999,
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  padding: '10px 16px',
  background: '#aa1a1d',
  color: '#fff',
  fontSize: 14,
  boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
};

const btnStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  padding: '5px 10px',
  borderRadius: 6,
  border: '1px solid rgba(255,255,255,0.5)',
  background: 'rgba(255,255,255,0.12)',
  color: '#fff',
  cursor: 'pointer',
  fontSize: 13,
  flexShrink: 0,
};

const screenStyle: CSSProperties = {
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 24,
  background: '#f5f3f0',
};

const cardStyle: CSSProperties = {
  maxWidth: 560,
  width: '100%',
  background: '#fff',
  borderRadius: 12,
  border: '1px solid #e5e0db',
  padding: 28,
  boxShadow: '0 8px 30px rgba(0,0,0,0.08)',
};

const preStyle: CSSProperties = {
  background: '#1e1e1e',
  color: '#e6e6e6',
  padding: 14,
  borderRadius: 8,
  fontSize: 13,
  overflowX: 'auto',
  margin: 0,
};
